import { FormControl, InputAdornment, Input } from '@material-ui/core';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { CommercialRootPath } from '../../components/dashboard/commercial/routeConfig';
import { useSchemeSearch } from '../../hooks/useSchemeData';
import { favLogos } from '../../logo/logos';
import { _debounce } from '../../utils/utilityFunctions';

import './searchinput.scss';

type SuggestionOptionType = {
    label: string;
    value: any;
    amcCode: string;
    schemeCategory: string;
    schemeSubCategory: string;
};
type SuggestionPropType = {
    options: SuggestionOptionType[];
    maxItems?: number;
    searchParam?: string;
};

const Suggestions = ({ options = [], maxItems = 100, searchParam }: SuggestionPropType) => {
    const history = useHistory();
    if (!searchParam) return null;
    if (searchParam && !options.length) {
        return (
            <div
                className="mf-search-dropdown-outer"
                style={{
                    position: 'absolute',
                    maxHeight: 300,
                    backgroundColor: 'white',
                    zIndex: 999,
                    minHeight: 50,
                    width: '100%',
                    border: '1px solid #eee',
                    borderRadius: '0 0 5px 5px',
                    padding: '15px',
                    color: '#2f363f',
                    fontSize: '16px',
                    overflowX: 'hidden',
                    boxShadow: '0 0 5px 0 #ccc',
                    transition: 'none',
                }}
            >
                {/* aria-live region for NVDA no results announcement */}
                <div
                    aria-live="polite"
                    aria-atomic="true"
                    style={{
                        position: 'absolute',
                        left: '-10000px',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                    }}
                >
                    No results found for {searchParam}
                </div>
                {`No match results for `}
                <span style={{ fontFamily: 'Montserrat-SemiBold' }}>{searchParam}</span>
            </div>
        );
    }
    return (
        <div
            className="mf-search-dropdown-outer"
            style={{
                position: 'absolute',
                maxHeight: 300,
                minHeight: 50,
                backgroundColor: 'white',
                zIndex: 999,
                width: '100%',
                border: '1px solid #eee',
                borderRadius: '0 0 5px 5px',
                boxShadow: '0 0 5px 0 #ccc',
                transition: 'none',
            }}
        >
            {/* aria-live region for NVDA search results announcement */}
            <div
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'absolute',
                    left: '-10000px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                }}
            >
                {options.length > maxItems
                    ? `${maxItems}+ results found for ${searchParam}`
                    : `${options.length} result${options.length !== 1 ? 's' : ''
                    } found for ${searchParam}`}
            </div>
            <p
                style={{
                    color: '#333333',
                    fontSize: 15,
                    padding: '10px 15px',
                    fontFamily: 'Montserrat-SemiBold',
                    borderBottom: '2px solid #eee',
                }}
            >
                {options.length > maxItems ? `${maxItems}+ results` : `${options.length} results`}
            </p>
            <ul
                style={{ maxHeight: 250, overflow: 'auto' }}
                tabIndex={0}
                aria-label="Search suggestions"
            >
                {options.slice(0, maxItems).map((ele: any) => (
                    <li>
                        <a
                            className="search-mf-links"
                            style={{
                                padding: '15px',
                                borderBottom: '1px solid #eee',
                                display: 'block',
                            }}
                            onClick={() => {
                                const url = `${CommercialRootPath}/${ele.value}/explore`;
                                history.push(url);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    history.push(`${CommercialRootPath}/${ele.value}/explore`);
                                }
                            }}
                            role="link"
                            tabIndex={0}
                        >
                            <h5
                                style={{
                                    fontSize: 16,
                                    color: '#424242',
                                    fontFamily: 'Montserrat-SemiBold',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <img
                                    style={{
                                        marginRight: 10,
                                        width: 30,
                                        height: 30,
                                        border: '1px solid #ccc',
                                        padding: 5,
                                        borderRadius: 5,
                                        background: '#fff',
                                    }}
                                    src={`/images/fav-logos/${favLogos[ele.amcCode]}.svg`}
                                    alt=""
                                />
                                {ele.label}
                            </h5>
                            <p style={{ marginLeft: 40 }}>
                                <span
                                    style={{
                                        fontSize: 12,
                                        lineHeight: '15px',
                                        color: '#000',
                                        opacity: 0.5,
                                        marginRight: 10,
                                        display: 'inline-block',
                                        marginBottom: 5,
                                        fontFamily: 'Montserrat-SemiBold',
                                    }}
                                >
                                    {ele.schemeCategory}
                                </span>
                                <span
                                    style={{
                                        fontSize: 12,
                                        lineHeight: '15px',
                                        color: '#000',
                                        opacity: 0.5,
                                        display: 'inline-block',
                                        fontFamily: 'Montserrat-SemiBold',
                                    }}
                                >
                                    {ele.schemeSubCategory.slice(
                                        ele.schemeSubCategory.indexOf(':') + 1,
                                    )}
                                </span>
                            </p>
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export function SchemeSearchInput({
    id = '',
    className = '',
    darkmode = false,
    placeholder = 'Search',
    value = '',
    onChange,
    searchKey = 'schemeName',
    ...rest
}: any) {
    const [show, setShow] = useState<boolean>(false);
    const [searchParam, setSearchParam] = useState<string>('');
    const [options, setOptions] = useState<SuggestionOptionType[]>([]);
    const { doGlobalSearch, searchData } = useSchemeSearch({});

    const callFilterFuncion = (e: any) => {
        doGlobalSearch(searchKey, e.target.value);
    };
    const _handleInputChange = (e: any) => {
        setSearchParam(e.target.value);
    };

    useEffect(() => {
        const options: SuggestionOptionType[] = searchData.map((fund: any) => ({
            label: fund[searchKey],
            value: fund.isinCode,
            amcCode: fund.fundAmcCode,
            schemeCategory: fund.schemeCategory,
            schemeSubCategory: fund.schemeSubCategory,
        }));
        setOptions(options);
    }, [searchData]);

    return (
        <FormControl className={className}>
            <Input
                id={id}
                type="text"
                placeholder={placeholder}
                autoComplete="off"
                startAdornment={
                    <InputAdornment position="start">
                        {darkmode ? (
                            <img
                                src="/images/search-icon-dark.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        ) : (
                            <img
                                src="/images/search-icon.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        )}
                    </InputAdornment>
                }
                onFocus={() => setShow(true)}
                // onBlur={() => {
                //     setShow(false);
                //     setOptions([]);
                // }}
                style={{
                    borderBottom: '1px solid #000',
                }}
                onChange={_debounce(callFilterFuncion, 500, _handleInputChange)}
                {...rest}
            />
            {/* {show && <Suggestions options={options} searchParam={searchParam} />} */}
            <Suggestions options={options} searchParam={searchParam} />
        </FormControl>
    );
}

export default function SearchInput({
    id = '',
    className = '',
    darkmode = false,
    placeholder = 'Search',
    value = '',
    onChange = (e: any) => { },
    searchKey = 'scheme_name',
    ...rest
}: any) {
    return (
        <FormControl className={className}>
            <Input
                id={id}
                type="text"
                placeholder={placeholder}
                autoComplete="off"
                startAdornment={
                    <InputAdornment position="start">
                        {darkmode ? (
                            <img
                                src="/images/search-icon-dark.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        ) : (
                            <img
                                src="/images/search-icon.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        )}
                    </InputAdornment>
                }
                onChange={onChange}
                style={{
                    borderBottom: '2px solid red !important',
                }}
                {...rest}
            />
        </FormControl>
    );
}
